import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";

export default function Episode() {
  const { id, episodeId } = useParams();
  const drama = dramas.find((item) => item.id === id);
  const index = drama ? drama.episodes.findIndex((item) => item.id === episodeId) : -1;
  const episode = drama && index >= 0 ? drama.episodes[index] : undefined;

  useEffect(() => {
    document.title =
      drama && episode ? `${episode.title} - ${drama.title} 分集剧情` : "章节不存在 - 迷人的保姆";
  }, [drama, episode]);

  if (!drama || !episode) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-900">该章节内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的章节可能已被移除，或链接有误。</p>
        <Link
          to={drama ? `/detail/${drama.id}` : "/category"}
          className="mt-5 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          {drama ? "返回剧集详情" : "返回剧集分类"}
        </Link>
      </div>
    );
  }

  const prev = index > 0 ? drama.episodes[index - 1] : undefined;
  const next = index < drama.episodes.length - 1 ? drama.episodes[index + 1] : undefined;

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 md:px-6">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/detail/${drama.id}`} className="transition-colors hover:text-primary-600">
          {drama.title}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">第 {episode.number} 集</span>
      </nav>

      <article className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative h-56 w-full overflow-hidden bg-background-200 md:h-72">
          <img
            src={episode.image}
            alt={`${drama.title} ${episode.title} 剧照`}
            title={`${episode.title} 分集剧照`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/25 to-transparent"></div>
          <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-primary-950">
              第 {episode.number} 集
            </span>
            <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
              {episode.title}
            </h1>
            <p className="mt-2 flex flex-wrap items-center gap-3 text-xs text-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {episode.airDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {episode.duration}
              </span>
            </p>
          </div>
        </div>

        <div className="p-4 md:p-6">
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
            <i className="ri-file-text-line text-primary-500"></i>
            本集剧情简介
          </h2>
          <p className="mt-2 rounded-lg bg-background-100 p-3 text-sm leading-relaxed text-foreground-700">
            {episode.summary}
          </p>

          <h2 className="mt-6 flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
            <i className="ri-book-2-line text-primary-500"></i>
            剧情详细内容
          </h2>
          <div className="mt-3 space-y-4 text-sm leading-7 text-foreground-700">
            {episode.content.split("。").filter(Boolean).map((sentence, i) => (
              <p key={i}>{sentence}。</p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
            <span className="flex items-center gap-2 text-xs text-foreground-600">
              <i className="ri-information-line text-primary-500"></i>
              本章节为剧情文字介绍，站内不提供视频播放功能。
            </span>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {prev ? (
              <Link
                to={`/detail/${drama.id}/episode/${prev.id}`}
                className="group flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg border border-background-200 p-3 transition-colors hover:border-primary-300"
              >
                <i className="ri-arrow-left-line text-foreground-400 transition-colors group-hover:text-primary-500"></i>
                <span className="min-w-0">
                  <span className="block text-[11px] text-foreground-500">上一集</span>
                  <span className="block truncate text-sm text-foreground-800">{prev.title}</span>
                </span>
              </Link>
            ) : (
              <div className="flex-1 rounded-lg border border-dashed border-background-200 p-3 text-center text-xs text-foreground-400">
                已经是第一集
              </div>
            )}

            {next ? (
              <Link
                to={`/detail/${drama.id}/episode/${next.id}`}
                className="group flex min-w-0 flex-1 cursor-pointer items-center justify-end gap-3 rounded-lg border border-background-200 p-3 text-right transition-colors hover:border-primary-300"
              >
                <span className="min-w-0">
                  <span className="block text-[11px] text-foreground-500">下一集</span>
                  <span className="block truncate text-sm text-foreground-800">{next.title}</span>
                </span>
                <i className="ri-arrow-right-line text-foreground-400 transition-colors group-hover:text-primary-500"></i>
              </Link>
            ) : (
              <div className="flex-1 rounded-lg border border-dashed border-background-200 p-3 text-center text-xs text-foreground-400">
                已经是最后一集
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <Link
              to={`/detail/${drama.id}`}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-go-back-line"></i>
              返回《{drama.title}》详情
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}